import { apiClient } from './client';

/**
 * 앱 배포 — 설정 › 다운로드 · 휴대폰 받기 화면 (2026-09-14 · 계약 `docs/api/app-releases-contract-2026-09-14.md`).
 *
 * ★ QR 은 **로그인이 필요한 웹 주소**(`/app/download/:platform`)를 가리킨다(사용자 결정) — 무로그인 토큰 링크는 쓰지 않는다.
 * ★ 받기는 302 가 아니라 JSON 이다(`POST …/download` → `{ downloadUrl }`) — 받은 주소로 이동한다.
 * ⚠ 서버 `app_releases` 가 아직 없는 서버는 404 를 준다 → 「아직 게시된 버전이 없음」 으로 본다(`unsupported`).
 */
export type AppPlatform = 'android' | 'ios';

export const APP_PLATFORMS: readonly AppPlatform[] = ['android', 'ios'];

export function isAppPlatform(v: string | undefined | null): v is AppPlatform {
  return !!v && (APP_PLATFORMS as readonly string[]).includes(v);
}

export interface AppReleaseDto {
  id: string;
  platform: 'ANDROID' | 'IOS';
  versionName: string;
  versionCode: number;
  buildNumber: string | null;
  fileName: string;
  sizeBytes: number;
  sha256: string;
  signingCertSha256: string | null;
  publishedAt: string;
  releaseNotes: string | null;
  minSupportedVersionCode: number | null;
}

/** 기관별 배포 방식 — `MDM` 이면 QR 카드 대신 관리자 문의 안내. 옛 서버(칸 없음)는 QR 로 본다. */
export interface AppDistributionDto {
  mode: 'QR' | 'MDM';
  mdmNote: string | null;
}

export interface LatestReleaseResult {
  release: AppReleaseDto | null;
  distribution: AppDistributionDto;
  /** 서버에 라우트가 없다(404) — 화면은 「아직 없음」 으로 그린다. */
  unsupported: boolean;
}

const DEFAULT_DISTRIBUTION: AppDistributionDto = { mode: 'QR', mdmNote: null };

/**
 * ★ 서버가 latest 를 `Cache-Control: private, max-age=300` 으로 준다(백엔드 답변). 받기 실패(회수 · 없음 · MDM) 뒤나
 *   「다시 시도」로 다시 읽을 때는 `fresh` 로 브라우저 캐시를 건너뛴다 — 안 그러면 5분 동안 회수된 게시본이 다시 선다.
 */
export async function getLatestAppRelease(platform: AppPlatform, opts?: { fresh?: boolean }): Promise<LatestReleaseResult> {
  try {
    const { data } = await apiClient.get<{ release: AppReleaseDto | null; distribution?: AppDistributionDto | null }>(
      '/app/releases/latest',
      opts?.fresh ? { params: { platform }, headers: { 'Cache-Control': 'no-cache' } } : { params: { platform } },
    );
    return {
      release: data?.release ?? null,
      distribution: data?.distribution?.mode === 'MDM' ? { mode: 'MDM', mdmNote: data.distribution.mdmNote ?? null } : DEFAULT_DISTRIBUTION,
      unsupported: false,
    };
  } catch (err) {
    const status = (err as { response?: { status?: number } } | null)?.response?.status;
    if (status === 404) return { release: null, distribution: DEFAULT_DISTRIBUTION, unsupported: true };
    throw err;
  }
}

export interface AppReleaseDownloadDto {
  downloadUrl: string;
  expiresAt: string;
  fileName: string;
  sizeBytes: number;
  sha256: string;
}

/** 받기 실패를 화면 말로 가른다 — 계약: 회수 410 `APP_RELEASE_REVOKED` · MDM 기관 403 `APP_DISTRIBUTION_MDM` · 없음 404. */
export type AppDownloadFailure = 'revoked' | 'gone' | 'mdm' | 'other';

export function appDownloadFailure(err: unknown): AppDownloadFailure {
  const res = (err as { response?: { status?: number; data?: { code?: string } } } | null)?.response;
  if (res?.status === 410 || res?.data?.code === 'APP_RELEASE_REVOKED') return 'revoked';
  if (res?.status === 403 && res?.data?.code === 'APP_DISTRIBUTION_MDM') return 'mdm';
  // 계약 문서 §3.2 는 「PUBLISHED 만 · 아니면 404」 — 서버가 회수본을 404 로 주더라도 최신을 다시 읽게 한다(#1301 리뷰).
  if (res?.status === 404) return 'gone';
  return 'other';
}

/**
 * 받은 주소로 이동하기 전 거르기 — http(s) 절대 주소나 같은 출처 경로(`/…`)만 통과(#1301 리뷰).
 * `javascript:` · `data:` 같은 주소가 오면 null — 호출부는 「받기 주소를 만들지 못했습니다」로 떨어진다.
 */
export function safeDownloadUrl(url: unknown, origin: string = window.location.origin): string | null {
  if (typeof url !== 'string' || !url.trim()) return null;
  try {
    const u = new URL(url, origin);
    return u.protocol === 'https:' || u.protocol === 'http:' ? u.href : null;
  } catch {
    return null;
  }
}

/** 5분짜리 받기 주소 — 서버가 감사를 남긴다. 없음 · 임시 404, 회수 410, MDM 기관 403. */
export async function requestAppReleaseDownload(id: string): Promise<AppReleaseDownloadDto> {
  const { data } = await apiClient.post<AppReleaseDownloadDto>(`/app/releases/${encodeURIComponent(id)}/download`);
  return data;
}

/** QR · 주소 복사가 담는 주소 — 로그인이 필요한 웹 화면. */
export function appDownloadPageUrl(platform: AppPlatform, origin: string = window.location.origin): string {
  return `${origin}/app/download/${platform}`;
}

/**
 * `215.1 MB` — 받기 카드 크기 칸.
 * ★ 2026-09-14 — **1000 기준 · 소수 한 자리 · MB**(관제 결정). 안드로이드 앱이 1000 기준이라 같은 APK 가 웹 205.2 MB(1024 기준) ·
 *   앱 215.1 MB 로 달리 보였다. 이 함수는 앱 받기 화면 둘(설정 › 다운로드 · /app/download)만 쓴다 — 다른 화면의 크기 표기는 그대로다.
 */
export function formatAppSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '—';
  // 반올림한 뒤 가른다 — 999,999 B 가 「1000 KB」 가 아니라 「1.0 MB」 로
  const mb = Math.round(bytes / 100_000) / 10;
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1000))} KB`;
}

/** 서명 지문 줄임 — `3A:9F:0C:…:E1:7B`(앞 3 · 뒤 2 바이트). 콜론 없는 16진이면 두 자씩 끊는다. */
export function shortFingerprint(hex: string | null): string | null {
  if (!hex) return null;
  const bytes = hex.includes(':') ? hex.split(':') : (hex.match(/.{1,2}/g) ?? []);
  const up = bytes.map((b) => b.toUpperCase());
  if (up.length <= 6) return up.join(':');
  return `${up.slice(0, 3).join(':')}:…:${up.slice(-2).join(':')}`;
}
